import {
  incumbentFieldsFromProfile,
  logProductProfile,
} from '@/lib/productProfileDebug';

// [2026-10-21] Was a bare 'http://localhost:4000' on localhost, which matched nothing:
// api/src/server.ts defaults to PORT 3000, so the local branch 503'd even with the API
// running, and /api/procurement/bootstrap returning 503 renders the whole Procurement
// page as zeros — indistinguishable from a tenant that is not wired. Honour
// VITE_API_BASE_URL (the pattern lib/api/exitLeverage.js already uses) and otherwise fall
// back to the deployed API, which is what lib/api/procurementServer.js does on both branches.
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? (import.meta.env.VITE_API_BASE_URL || 'https://api.neogtm.com')
  : 'https://api.neogtm.com';

const BASE = `${API_BASE_URL}/api`;

function headers(tenantId) {
  return {
    'Content-Type': 'application/json',
    'x-tenant-id': tenantId,
  };
}

export async function fetchFitBulkLatest(tenantId, dealIds) {
  const ids = Array.isArray(dealIds) ? dealIds.filter(Boolean) : [];

  if (!tenantId) {
    throw new Error('tenantId is required');
  }

  if (ids.length === 0) {
    return {};
  }

  const response = await fetch(`${BASE}/fit/bulk-latest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
    },
    credentials: 'include',
    body: JSON.stringify({
      tenant_id: tenantId,
      dealIds: ids,
    }),
  });

  const raw = await response.text();

  let data;
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = raw;
  }

  if (!response.ok) {
    throw new Error(
      typeof data === 'object' && data?.error
        ? data.error
        : `bulk-latest failed (${response.status})`
    );
  }

  return data || {};
}

export async function fetchFitLatest(tenantId, dealId) {
  try {
    const encoded = encodeURIComponent(dealId);
    const res = await fetch(`${BASE}/fit/deals/${encoded}/latest`, {
      headers: headers(tenantId),
      credentials: 'include',
    });
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error(`Fit fetch failed: ${res.status}`);
    }
    return res.json();
  } catch (err) {
    return null;
  }
}

/** POST — queues light fit recompute; pair with GET fetchFitLatest for current state. */
export async function refreshLightFit(tenantId, dealId) {
  if (!dealId) {
    throw new Error('dealId is required');
  }

  if (!tenantId) {
    throw new Error('tenantId is required');
  }

  const encoded = encodeURIComponent(dealId);
  const response = await fetch(`${BASE}/fit/deals/${encoded}/light/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
    },
    credentials: 'include',
    body: JSON.stringify({}),
  });

  const raw = await response.text();

  let data;
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = raw;
  }

  if (!response.ok) {
    throw new Error(
      typeof data === 'object' && data?.error
        ? data.error
        : `light refresh failed (${response.status})`
    );
  }

  return data || {};
}

export async function triggerDeepFit(tenantId, dealId, profileId, uploadedFileIds) {
  const res = await fetch(`${BASE}/fit/deals/${dealId}/deep/run`, {
    method: 'POST',
    headers: headers(tenantId),
    credentials: 'include',
    body: JSON.stringify({ profile_id: profileId, uploaded_file_ids: uploadedFileIds }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || `Deep fit trigger failed: ${res.status}`);
  }
  return res.json();
}

export async function pollFitJob(tenantId, jobId) {
  const res = await fetch(`${BASE}/fit/jobs/${jobId}`, {
    headers: headers(tenantId),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Job poll failed: ${res.status}`);
  return res.json();
}

export async function registerTenderDocs(tenantId, dealId, files) {
  const res = await fetch(`${BASE}/fit/deals/${dealId}/deep/upload/register`, {
    method: 'POST',
    headers: headers(tenantId),
    credentials: 'include',
    body: JSON.stringify({ files }),
  });
  if (!res.ok) throw new Error(`Upload register failed: ${res.status}`);
  return res.json();
}

export async function uploadTenderDocs(tenantId, dealId, files) {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  const res = await fetch(`${BASE}/fit/deals/${dealId}/deep/upload`, {
    method: 'POST',
    headers: { 'x-tenant-id': tenantId },
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Upload failed');
  return res.json();
}

export async function fetchTenderDocs(tenantId, dealId) {
  const encoded = encodeURIComponent(dealId);
  const res = await fetch(`${BASE}/fit/deals/${encoded}/docs`, {
    headers: headers(tenantId),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Docs fetch failed: ${res.status}`);
  return res.json();
}

/** DELETE tender doc — returns HTTP status (2xx or 404 both mean success for UI refresh). */
export async function deleteTenderDoc(tenantId, dealId, docId) {
  const encodedDealId = encodeURIComponent(dealId);
  const encodedDocId = encodeURIComponent(docId);
  const res = await fetch(`${BASE}/fit/deals/${encodedDealId}/docs/${encodedDocId}`, {
    method: 'DELETE',
    headers: headers(tenantId),
    credentials: 'include',
  });
  return res.status;
}

export async function fetchProductProfiles(tenantId) {
  logProductProfile('api.fetch_list.start', { tenantId });
  const res = await fetch(`${BASE}/settings/product-profiles`, {
    headers: headers(tenantId),
    credentials: 'include',
  });
  if (!res.ok) {
    logProductProfile('api.fetch_list.error', { tenantId, status: res.status });
    throw new Error(`Profile fetch failed: ${res.status}`);
  }
  const data = await res.json();
  const list = Array.isArray(data) ? data : [];
  logProductProfile('api.fetch_list.ok', {
    tenantId,
    count: list.length,
    profiles: list.map((p) => ({
      id: p.id,
      name: p.name,
      is_active: p.is_active,
      ...incumbentFieldsFromProfile(p),
    })),
  });
  return data;
}

export async function createProductProfile(tenantId, profile) {
  logProductProfile('api.create.start', {
    tenantId,
    name: profile?.name,
    requestIncumbent: incumbentFieldsFromProfile(profile),
  });
  const res = await fetch(`${BASE}/settings/product-profiles`, {
    method: 'POST',
    headers: headers(tenantId),
    credentials: 'include',
    body: JSON.stringify(profile),
  });
  if (!res.ok) {
    logProductProfile('api.create.error', { tenantId, status: res.status });
    throw new Error(`Profile create failed: ${res.status}`);
  }
  const data = await res.json();
  logProductProfile('api.create.ok', {
    tenantId,
    profileId: data?.id,
    responseIncumbent: incumbentFieldsFromProfile(data),
  });
  return data;
}

export async function updateProductProfile(tenantId, id, updates) {
  logProductProfile('api.update.start', {
    tenantId,
    profileId: id,
    requestIncumbent: incumbentFieldsFromProfile(updates),
  });
  const res = await fetch(`${BASE}/settings/product-profiles/${id}`, {
    method: 'PATCH',
    headers: headers(tenantId),
    credentials: 'include',
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    logProductProfile('api.update.error', {
      tenantId,
      profileId: id,
      status: res.status,
      error: err?.error || err?.message,
    });
    throw new Error(err?.error || err?.message || `Profile update failed: ${res.status}`);
  }
  const data = await res.json();
  logProductProfile('api.update.ok', {
    tenantId,
    profileId: id,
    responseIncumbent: incumbentFieldsFromProfile(data),
  });
  return data;
}

/** POST /activate — only toggles this profile; does not use PATCH for is_active. */
export async function activateProductProfile(tenantId, id) {
  const res = await fetch(`${BASE}/settings/product-profiles/${id}/activate`, {
    method: 'POST',
    headers: headers(tenantId),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || err?.message || `Profile activate failed: ${res.status}`);
  }
  return res.json().catch(() => ({}));
}

/** POST /deactivate — only toggles this profile; does not use PATCH for is_active. */
export async function deactivateProductProfile(tenantId, id) {
  const res = await fetch(`${BASE}/settings/product-profiles/${id}/deactivate`, {
    method: 'POST',
    headers: headers(tenantId),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || err?.message || `Profile deactivate failed: ${res.status}`);
  }
  return res.json().catch(() => ({}));
}

/** Delegates to POST /activate or /deactivate — never PATCH is_active. */
export async function setProductProfileIsActive(tenantId, id, isActive) {
  return isActive ? activateProductProfile(tenantId, id) : deactivateProductProfile(tenantId, id);
}

export async function deleteProductProfile(tenantId, id) {
  const res = await fetch(`${BASE}/settings/product-profiles/${id}`, {
    method: 'DELETE',
    headers: headers(tenantId),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Profile delete failed: ${res.status}`);
}
